import { useCallback } from "react";
import { useOrgPublicKey } from "~/hooks/useOrgEncryption";
import { useEncryption, base64ToUint8Array } from "~/hooks/useEncryption";
import {
  generatePerReportDek,
  wrapDekForUser,
  bufferToBase64,
  unwrapDekFromUser,
} from "~/helpers/envelopeCrypto";
import { importRsaPublicKey, rsaOaepWrapDek } from "~/helpers/orgCrypto";
import { usePassword } from "~/store/credentials";
import type { EncryptedPayload } from "~/types/global";
import { CRYPTO_SERVICE, type CryptoResult } from "~/workers/crypto";

/** Campos de envelope do payload. `null` quando a org não tem chave ativa (usuário standalone/legado). */
export type EnvelopeFields = {
  user_wrapped_dek: string | null;
  user_dek_iv: string | null;
  org_wrapped_dek: string | null;
  key_version: number | null;
};

const NULL_ENVELOPE: EnvelopeFields = {
  user_wrapped_dek: null,
  user_dek_iv: null,
  org_wrapped_dek: null,
  key_version: null,
};

export type EncryptWithEnvelopeResult = {
  encrypted: CryptoResult<EncryptedPayload>;
  envelope: EnvelopeFields;
};

/** Fonte única de criptografia envelope: garante que itens cifráveis recebam envelope e não sejam tratados como legado */
export function useEnvelopeEncryption() {
  const { data: orgPublicKey } = useOrgPublicKey();
  const { _encryptData, decryptData, deriveKey } = useEncryption();
  const secretKey = usePassword();

  const rawUserKey = useCallback((): ArrayBuffer => {
    const userDek = base64ToUint8Array(secretKey || "");
    return userDek.buffer.slice(
      userDek.byteOffset,
      userDek.byteOffset + userDek.byteLength,
    ) as ArrayBuffer;
  }, [secretKey]);

  /** Gera e encapsula um DEK por item (usuário + org). Sem org key retorna `dek: null` e envelope nulo. */
  const generateEnvelope = useCallback(async (): Promise<{ dek: Uint8Array | null; envelope: EnvelopeFields }> => {
    if (!orgPublicKey) {
      return { dek: null, envelope: { ...NULL_ENVELOPE } };
    }

    const dek = generatePerReportDek();
    const wrapped = await wrapDekForUser(rawUserKey(), dek);
    const rsaPubKey = await importRsaPublicKey(orgPublicKey.public_key);
    const orgWrappedBuf = await rsaOaepWrapDek(rsaPubKey, dek);

    return {
      dek,
      envelope: {
        user_wrapped_dek: wrapped.user_wrapped_dek,
        user_dek_iv: wrapped.user_dek_iv,
        org_wrapped_dek: bufferToBase64(new Uint8Array(orgWrappedBuf)),
        key_version: orgPublicKey.key_version,
      },
    };
  }, [orgPublicKey, rawUserKey]);

  /** Cifra `plaintext` e retorna a cifra + envelope. Sem org key cifra direto com a chave do usuário (legado). */
  const encryptWithEnvelope = useCallback(async (plaintext: unknown): Promise<EncryptWithEnvelopeResult> => {
    const { dek, envelope } = await generateEnvelope();
    const key = dek
      ? (dek.buffer as ArrayBuffer)
      : (base64ToUint8Array(secretKey || "").buffer as ArrayBuffer);
    const encrypted = await _encryptData(plaintext, key);
    return { encrypted, envelope };
  }, [generateEnvelope, _encryptData, secretKey]);

  /** Decryptor para itens com envelope (desembrulha o DEK via WASM). Sem envelope, usa o `decryptData` legado. */
  const createItemDecryptor = useCallback(
    (item: { user_wrapped_dek?: string | null; user_dek_iv?: string | null }) => {
      if (!item.user_wrapped_dek || !item.user_dek_iv) {
        return decryptData;
      }

      let cachedDek: Uint8Array | null = null;

      return async (encryptedField: EncryptedPayload): Promise<CryptoResult<any>> => {
        try {
          if (!cachedDek) {
            const userDek = await deriveKey();
            const rawKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
            cachedDek = await unwrapDekFromUser(rawKey, item.user_wrapped_dek!, item.user_dek_iv!);
          }

          const ciphertext = base64ToUint8Array(encryptedField.encrypted);
          const iv = base64ToUint8Array(encryptedField.iv);
          const { decrypted } = await CRYPTO_SERVICE.decrypt(cachedDek, iv, ciphertext);
          return { success: true, data: decrypted };
        } catch (error) {
          return { success: false, error: error instanceof Error ? error.message : "Envelope decryption error" };
        }
      };
    },
    [decryptData, deriveKey],
  );

  return { hasOrgEncryption: !!orgPublicKey, generateEnvelope, encryptWithEnvelope, createItemDecryptor };
}
